import React, { useLayoutEffect, useRef } from "react";

type OverlayScrollProps = React.ComponentProps<"div"> & {
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  /// The wrapper the thumb is placed in, where a flex parent's sizing goes.
  frameClassName?: string;
};

/** Scrolls under a thumb drawn over its right edge: a native bar takes width,
 *  which would pull the rows off a header that sits outside the scroller. */
export const OverlayScroll: React.FC<OverlayScrollProps> = ({
  scrollRef,
  frameClassName = "",
  className = "",
  children,
  ...rest
}) => {
  const ownRef = useRef<HTMLDivElement>(null);
  const box = scrollRef ?? ownRef;
  const content = useRef<HTMLDivElement>(null);
  const thumb = useRef<HTMLDivElement>(null);
  // Scroll per pixel of thumb travel, as last laid out.
  const rate = useRef(1);
  const drag = useRef({ y: 0, top: 0 });

  useLayoutEffect(() => {
    const el = box.current!;
    const bar = thumb.current!;
    const place = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      bar.hidden = scrollHeight <= clientHeight;
      if (bar.hidden) return;
      const min = parseFloat(getComputedStyle(bar).minHeight) || 0;
      const length = Math.max(
        (clientHeight * clientHeight) / scrollHeight,
        min,
      );
      rate.current =
        (scrollHeight - clientHeight) / Math.max(1, clientHeight - length);
      bar.style.height = `${length}px`;
      bar.style.top = `${scrollTop / rate.current}px`;
    };
    place();
    el.addEventListener("scroll", place, { passive: true });
    const observer = new ResizeObserver(place);
    observer.observe(el);
    observer.observe(content.current!);
    return () => {
      el.removeEventListener("scroll", place);
      observer.disconnect();
    };
  }, [box]);

  return (
    <div className={`relative ${frameClassName}`}>
      <div
        ref={box}
        {...rest}
        className={`${className} overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
      >
        <div ref={content}>{children}</div>
      </div>
      <div
        ref={thumb}
        aria-hidden
        data-testid="overlay-thumb"
        className="scrollbar-thumb absolute right-0 z-20 w-(--size-scrollbar) min-h-(--size-scrollbar-thumb-min)"
        onPointerDown={(e) => {
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { y: e.clientY, top: box.current!.scrollTop };
        }}
        onPointerMove={(e) => {
          if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
          box.current!.scrollTop =
            drag.current.top + (e.clientY - drag.current.y) * rate.current;
        }}
      />
    </div>
  );
};
